import savedCompositionsIcon from '../../../assets/icons/saved_designs.svg';
import { Step } from "zakeke-configurator-react/dist/declarations/composer/Module/src/models";
import Designer from '../Designer';
import { useState, useEffect } from 'react';
import { useZakeke, Option } from 'zakeke-configurator-react';
import { Template, AttributeName, TemplatesContainer, SelectorMobileContainer, PriceInfoTextContainer, StepsMobileContainer } from '../LayoutStyled';
import { getDefinitiveGroups, useUndoRedoActions, useUndoRegister } from 'Helpers';
import Steps from '../Steps';
import useStore from 'Store';
import { MobileItemsContainer } from './SelectorMobileComponents';
import { MenuItem } from './SelectorMobileComponents';
import DesignsDraftList from '../DesignsDraftList';
import star from '../../../assets/icons/star.svg';
import noImage from '../../../assets/images/no_image.png';
import { act } from 'react-dom/test-utils';

const SelectorMobile = () => {

    const designerStep: Step = {
        id: -2,
        guid: "0000-0000-0000-0003",
        name: "designer-step",
        displayOrder: 1,
        attributes: []
    }

    const { isSceneLoading, isAreaVisible, groups, product, templates, currentTemplate, setCamera, setTemplate, sellerSettings, selectOption, draftCompositions } = useZakeke();
    const { selectedGroupId, setSelectedGroupId, selectedAttributeId, setSelectedAttributeId, selectedStepId, setSelectedStepId, isUndo, isRedo } = useStore();
    const [scrollLeft, setScrollLeft] = useState<number | null>(null);
    const [optionsScroll, setOptionsScroll] = useState<number | null>(null);
    const [attributesScroll, setAttributesScroll] = useState<number | null>(null);
    const [isTemplateEditorOpened, setIsTemplateEditorOpened] = useState(false);
    const [isDesignsDraftListOpened, setisDesignsDraftListOpened] = useState(false);
    const shouldCustomizerGroupBeVisible = !isSceneLoading && product ? product.areas.some(area => isAreaVisible(area.id)) : false;
    const [isStartRegistering, setIsStartRegistering] = useState(false);
    const undoRegistering = useUndoRegister();
    const undoRedoActions = useUndoRedoActions();
    const hasDesignsSaved = (groups && draftCompositions && draftCompositions.length > 0) ?? false;

    const actualGroups = getDefinitiveGroups(groups, false, hasDesignsSaved, sellerSettings) ?? [];
    if (actualGroups.length > 0) {
        const indexStep = groups[0].steps[groups[0].steps.length - 1].name.toLowerCase().indexOf("custom") ? groups[0].steps.length - 2 : groups[0].steps.length - 1;
        const yesOption = groups[0].steps[indexStep].attributes[0].options.find(x => x.name.toLowerCase() === 'yes' && x.enabled);
        if (yesOption?.selected)
            actualGroups[0].steps.push(designerStep);
    }
    const selectedCurrentGroup = selectedGroupId ? actualGroups.find(group => group.id === selectedGroupId) : null;
    const selectedCurrentStep = selectedGroupId ? actualGroups.find(group => group.id === selectedGroupId)?.steps.find(step => step.id === selectedStepId) : null;
    const currentAttributes = selectedCurrentStep ? selectedCurrentStep.attributes : (selectedCurrentGroup ? selectedCurrentGroup.attributes : []);
    const selectedCurrentAttribute = currentAttributes ? currentAttributes.find(attr => attr.id === selectedAttributeId) : null;
    const options = selectedCurrentAttribute?.options ?? [];
    const groupIndex = actualGroups && selectedCurrentGroup ? actualGroups.indexOf(selectedCurrentGroup) : 0;

    const handleNextGroup = () => {
        if (selectedCurrentGroup) {
            if (groupIndex < actualGroups.length - 1) {
                const nextGroup = actualGroups[groupIndex + 1];
                handleGroupSelection(nextGroup.id);
            }
        }
    }

    const handlePreviousGroup = () => {
        if (selectedCurrentGroup) {
            if (groupIndex > 0) {
                let previousGroup = actualGroups[groupIndex - 1];
                handleGroupSelection(previousGroup.id);

                // Select the last step
                if (previousGroup.steps.length > 0)
                    handleStepSelection(previousGroup.steps[previousGroup.steps.length - 1].id);
            }
        }
    }

    const handleStepChange = (step: Step | null) => {
        if (step)
            handleStepSelection(step.id)
    }

    const handleGroupSelection = (groupId: number | null) => {

        setIsStartRegistering(undoRegistering.startRegistering());

        if (groupId && selectedGroupId !== groupId && !isUndo && !isRedo) {
            undoRedoActions.eraseRedoStack();
            undoRedoActions.fillUndoStack({ type: "group", id: selectedGroupId, direction: "undo" });
            undoRedoActions.fillUndoStack({ type: "group", id: groupId, direction: "redo" });
        }

        setSelectedGroupId(groupId);
    }

    const handleStepSelection = (stepId: number | null) => {

        setIsStartRegistering(undoRegistering.startRegistering());

        if (selectedStepId !== stepId && !isUndo && !isRedo) {
            undoRedoActions.eraseRedoStack();
            undoRedoActions.fillUndoStack({ type: "step", id: selectedStepId, direction: "undo" });
            undoRedoActions.fillUndoStack({ type: "step", id: stepId ?? null, direction: "redo" });
        }

        setSelectedStepId(stepId);
    }

    const handleAttributeSelection = (attributeId: number) => {

        setIsStartRegistering(undoRegistering.startRegistering());

        if (attributeId && selectedAttributeId !== attributeId && !isUndo && !isRedo) {
            undoRedoActions.eraseRedoStack();
            undoRedoActions.fillUndoStack({ type: "attribute", id: selectedAttributeId, direction: "undo" });
            undoRedoActions.fillUndoStack({ type: "attribute", id: attributeId, direction: "redo" });
        }

        setSelectedAttributeId(attributeId);
    }

    const handleOptionSelection = (option: Option) => {
        const undo = undoRegistering.startRegistering();
        undoRedoActions.eraseRedoStack();
        undoRedoActions.fillUndoStack({ type: "option", id: options.find(opt => opt.selected)!.id, direction: "undo" });
        undoRedoActions.fillUndoStack({ type: "option", id: option.id, direction: "redo" });

        setCamera(option.attribute.cameraLocationId!);
        selectOption(option.id);
        undoRegistering.endRegistering(undo);
    }

    useEffect(() => {
        if (selectedGroupId == null && actualGroups.length > 0)
            setSelectedGroupId(actualGroups[0].id)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGroupId, actualGroups]);

    useEffect(() => {
        if (!selectedCurrentGroup && groups.length > 0) {
            if (templates.length > 0)
                setTemplate(templates[0].id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCurrentGroup, groups, templates]);

    // Initial template selection
    useEffect(() => {
        if (templates.length > 0 && !currentTemplate)
            setTemplate(templates[0].id);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templates]);

    // Reset attribute selection when group selection changes
    useEffect(() => {
        if (selectedCurrentGroup && selectedCurrentGroup.id !== -2) {
            if (selectedCurrentGroup.steps.length > 0) {
                handleStepSelection(selectedCurrentGroup.steps[0].id);
                if (selectedCurrentGroup.steps[0].attributes.length === 1)
                    handleAttributeSelection(selectedCurrentGroup.steps[0].attributes[0].id);
            }
            else {
                handleStepSelection(null);
                if (selectedCurrentGroup.attributes.length === 1)
                    handleAttributeSelection(selectedCurrentGroup.attributes[0].id);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCurrentGroup?.id]);

    useEffect(() => {
        if (selectedCurrentGroup?.id === -2) {
            setIsTemplateEditorOpened(true);
        }
    }, [selectedCurrentGroup?.id]);

    useEffect(() => {
        if (selectedCurrentGroup?.id === -3) {
            setisDesignsDraftListOpened(true);
        }
    }, [selectedCurrentGroup?.id]);


    // Camera
    useEffect(() => {
        if (!isSceneLoading && selectedCurrentGroup && selectedCurrentGroup.cameraLocationId) {
            setCamera(selectedCurrentGroup.cameraLocationId);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCurrentGroup?.id, isSceneLoading]);

    useEffect(() => {
        if (selectedCurrentGroup && selectedCurrentGroup.steps.length > 0) {
            if (selectedCurrentGroup.steps.find(step => step.id === selectedCurrentStep?.id))
                if (selectedCurrentStep && selectedCurrentStep.id !== -2)
                    handleAttributeSelection(selectedCurrentGroup.steps!.find(step => step.id === selectedCurrentStep?.id)!.attributes[0].id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCurrentStep?.id]);

    useEffect(() => {
        if (isStartRegistering) {
            undoRegistering.endRegistering(false);
            setIsStartRegistering(false);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStartRegistering]);


    useEffect(() => {
        if (selectedStepId === -2) {
            setIsTemplateEditorOpened(true);
        }
    }, [selectedStepId]);

    return <SelectorMobileContainer>
        {sellerSettings && sellerSettings.priceInfoText && <PriceInfoTextContainer>{sellerSettings.priceInfoText}</PriceInfoTextContainer>}
        {selectedCurrentGroup && selectedCurrentGroup.id !== -2 && selectedCurrentGroup.steps && selectedCurrentGroup.steps.length > 0 &&
            <StepsMobileContainer>
                <Steps
                    key={"steps-" + selectedCurrentGroup?.id}
                    hasNextGroup={groupIndex !== actualGroups.length - 1}
                    hasPreviousGroup={groupIndex !== 0}
                    onNextStep={handleNextGroup}
                    onPreviousStep={handlePreviousGroup}
                    currentStep={selectedCurrentStep}
                    steps={selectedCurrentGroup.steps}
                    onStepChange={handleStepChange}
                />
            </StepsMobileContainer>
        }
        {selectedStepId !== -2 && selectedCurrentGroup == null && <MobileItemsContainer visibilityLeft visibilityRight scrollLeft={scrollLeft ?? 0} onScrollChange={value => setScrollLeft(value)} >
            {actualGroups.map(group => {

                if (group)
                    return <MenuItem
                        key={group.guid}
                        imageUrl={group.id === -3 ? savedCompositionsIcon : (group.imageUrl ? group.imageUrl : star)}
                        label={group.id === -1 ? "Configure" : group.name}
                        onClick={() => handleGroupSelection(group.id)}
                    >
                    </MenuItem>
                else
                    return null;
            })}
        </MobileItemsContainer>}

        {/* <AttributesContainer > */}
        {selectedStepId !== -2 && selectedCurrentGroup && selectedCurrentGroup.id === -2 && templates.length > 1 &&
            <TemplatesContainer>
                {templates.map(template => <Template
                    key={template.id}
                    selected={currentTemplate === template}
                    onClick={() => {
                        setTemplate(template.id);
                    }}
                >
                    {template.name}
                </Template>)}
            </TemplatesContainer>}

        {selectedStepId !== -2 && selectedCurrentGroup && <MobileItemsContainer visibilityLeft visibilityRight
            scrollLeft={attributesScroll ?? 0}
            onScrollChange={value => setAttributesScroll(value)}>

            {/* Attributes */}

            {selectedCurrentGroup && !selectedAttributeId && currentAttributes && currentAttributes.map(attribute => {
                return <MenuItem
                    selected={attribute.id === selectedAttributeId}
                    key={attribute.guid}
                    onClick={() => handleAttributeSelection(attribute.id)}
                    images={attribute.options.slice(0, 4).map(x => x.imageUrl ? x.imageUrl : noImage)}
                    label={attribute.name}
                    isRound={attribute.optionShapeType === 2}
                >
                    <AttributeName> {attribute.name.toUpperCase()} </AttributeName>
                </MenuItem>
            })}
            {/* </CarouselContainer> */}

            {/* Options */}
            <MobileItemsContainer
                visibilityLeft={options.length !== 0}
                visibilityRight={options.length !== 0}
                scrollLeft={optionsScroll ?? 0}
                onScrollChange={value => setOptionsScroll(value)} >
                {selectedCurrentAttribute && selectedCurrentAttribute.options.map(option => option.enabled &&
                    <MenuItem
                        isRound={selectedCurrentAttribute.optionShapeType === 2}
                        description={option.description}
                        selected={option.selected}
                        imageUrl={option.imageUrl ? option.imageUrl : noImage}
                        label={option.name}
                        hideLabel={selectedCurrentAttribute.hideOptionsLabel}
                        key={option.guid}
                        onClick={() => handleOptionSelection(option)} />)}
            </MobileItemsContainer>
        </MobileItemsContainer>
        }

        {/* Designer / Customizer */}
        {selectedStepId === -2 && isTemplateEditorOpened && <Designer onCloseClick={() => { setIsTemplateEditorOpened(false); handleStepSelection(selectedCurrentGroup!.steps[selectedCurrentGroup!.steps.length - 2].id) }} isMobile={window.innerWidth < 768} />}

        {/* Saved Compositions */}
        {draftCompositions && selectedCurrentGroup?.id === -3 && isDesignsDraftListOpened && <DesignsDraftList onCloseClick={() => { setIsTemplateEditorOpened(false); handleGroupSelection(null) }} />}


    </SelectorMobileContainer>
}

export default SelectorMobile;